import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { 
  Menu, 
  Compass, 
  Package, 
  Users, 
  Sparkles,
  Stethoscope,
  Landmark,
  Building2,
  Shield,
  Search,
  Code,
  TestTube,
  type LucideIcon
} from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { useContactForm } from "@/components/ContactFormModal";
import logo from "@/assets/logo.png";

interface NavItemConfig {
  titleKey: string;
  descriptionKey: string;
  href: string;
  icon: LucideIcon;
}

const servicesConfig: NavItemConfig[] = [
  {
    titleKey: "services.aiDiscovery.title",
    descriptionKey: "services.aiDiscovery.description",
    href: "/services/consulting",
    icon: Compass,
  },
  {
    titleKey: "services.fixedPrice.title",
    descriptionKey: "services.fixedPrice.description",
    href: "/services/fixed-price",
    icon: Package,
  },
  {
    titleKey: "services.enablement.title",
    descriptionKey: "services.enablement.description",
    href: "/services/enablement",
    icon: Sparkles,
  },
  {
    titleKey: "services.teams.title",
    descriptionKey: "services.teams.description",
    href: "/services/teams",
    icon: Users,
  },
];

const industriesConfig: NavItemConfig[] = [
  { 
    titleKey: "industries.healthcare.title",
    descriptionKey: "industries.healthcare.description",
    href: "/industries/pharma",
    icon: Stethoscope,
  },
  { 
    titleKey: "industries.finance.title",
    descriptionKey: "industries.finance.description",
    href: "/industries/finance",
    icon: Landmark,
  },
  { 
    titleKey: "industries.enterprise.title",
    descriptionKey: "industries.enterprise.description",
    href: "/industries/enterprise",
    icon: Building2,
  },
  { 
    titleKey: "industries.defence.title",
    descriptionKey: "industries.defence.description",
    href: "/industries/defence",
    icon: Shield,
  },
];

const openSourceConfig: NavItemConfig[] = [
  {
    titleKey: "openSourceTools.webSearch.title",
    descriptionKey: "openSourceTools.webSearch.description",
    href: "/open-source/kindly-web-search",
    icon: Search,
  },
  {
    titleKey: "openSourceTools.codeReview.title",
    descriptionKey: "openSourceTools.codeReview.description",
    href: "/open-source/lad-code-review",
    icon: Code,
  },
  {
    titleKey: "openSourceTools.tdd.title",
    descriptionKey: "openSourceTools.tdd.description",
    href: "/open-source/tdd",
    icon: TestTube,
  },
];

function BookACallButton({ className, size }: { className?: string; size?: "default" | "sm" | "lg" | "icon" }) {
  const { t } = useTranslation();
  const { openContactForm } = useContactForm();
  return (
    <Button size={size} className={className} onClick={() => openContactForm({ page: "Header", button: "Book a Call" })}>
      {t("common.bookACall")}
    </Button>
  );
}

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();

  const getHref = (path: string) => {
    if (path.startsWith("http")) return path;
    return getLocalizedPath(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="container flex h-20 lg:h-24 items-center justify-between gap-4">
        {/* Logo */}
        <a href={getLocalizedPath("/")} className="flex items-center group shrink-0">
          <img
            src={logo}
            alt="Shelpuk & Company"
            width={400}
            height={74}
            decoding="async"
            className="h-10 lg:h-14 w-auto max-w-[160px] lg:max-w-[200px] object-contain translate-y-0.5"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:justify-center lg:flex-1 lg:gap-1 lg:mt-[28px]">
          <NavigationMenu>
            <NavigationMenuList>
              {/* Services Mega Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-sm font-medium text-muted-foreground hover:text-foreground">
                  {t("nav.services")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-2 p-4">
                    {servicesConfig.map((service) => (
                      <li key={service.titleKey}>
                        <NavigationMenuLink asChild>
                          <a
                            href={getHref(service.href)}
                            className="group flex items-start gap-4 rounded-lg p-3 transition-colors hover:bg-muted"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary group-hover:bg-primary/10">
                              <service.icon className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-foreground">{t(service.titleKey)}</div>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {t(service.descriptionKey)}
                              </p>
                            </div>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Industries Mega Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-sm font-medium text-muted-foreground hover:text-foreground">
                  {t("nav.industries")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-2 p-4">
                    {industriesConfig.map((industry) => (
                      <li key={industry.titleKey}>
                        <NavigationMenuLink asChild>
                          <a
                            href={getHref(industry.href)}
                            className="group flex items-start gap-4 rounded-lg p-3 transition-colors hover:bg-muted"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary group-hover:bg-primary/10">
                              <industry.icon className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-foreground">{t(industry.titleKey)}</div>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {t(industry.descriptionKey)}
                              </p>
                            </div>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Open Source Mega Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-sm font-medium text-muted-foreground hover:text-foreground">
                  {t("nav.openSource")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[420px] gap-2 p-4">
                    {openSourceConfig.map((tool) => {
                      const isExternal = tool.href.startsWith("http");
                      return (
                        <li key={tool.titleKey}>
                          <NavigationMenuLink asChild>
                            <a
                              href={isExternal ? tool.href : getHref(tool.href)}
                              target={isExternal ? "_blank" : undefined}
                              rel={isExternal ? "noopener noreferrer" : undefined}
                              className="group flex items-start gap-4 rounded-lg p-3 transition-colors hover:bg-muted"
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary group-hover:bg-primary/10">
                                <tool.icon className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-foreground">{t(tool.titleKey)}</div>
                                <p className="text-sm text-muted-foreground mt-0.5">
                                  {t(tool.descriptionKey)}
                                </p>
                              </div>
                            </a>
                          </NavigationMenuLink>
                        </li>
                      );
                    })}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink
                  href={getHref("/sovereign-ai")}
                  className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t("nav.euPrivacy")}
                </NavigationMenuLink>
              </NavigationMenuItem>

              {/* Case Studies Link */}
              <NavigationMenuItem>
                <NavigationMenuLink
                  href={getHref("/case-studies")}
                  className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t("nav.caseStudies")}
                </NavigationMenuLink>
              </NavigationMenuItem>

              {/* Insights Link */}
              <NavigationMenuItem>
                <NavigationMenuLink
                  href={getHref("/insights")}
                  className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t("nav.insights")}
                </NavigationMenuLink>
              </NavigationMenuItem>

              {/* Blog Link */}
              <NavigationMenuItem>
                <NavigationMenuLink
                  href={getHref("/blog")}
                  className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t("nav.blog")}
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Location + Language + CTA */}
        <div className="flex items-center gap-2 mt-3 lg:mt-[28px] shrink-0">
          <LanguageSwitcher />
          <BookACallButton size="sm" className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm" />

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">{t("header.toggleMenu")}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[380px] border-l border-border/50 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <SheetTitle className="sr-only">{t("header.navigationMenu")}</SheetTitle>
              <nav className="flex flex-col gap-6 mt-8">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("nav.services")}</p>
                  {servicesConfig.map((service) => (
                    <a
                      key={service.titleKey}
                      href={getHref(service.href)}
                      className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {t(service.titleKey)}
                    </a>
                  ))}
                </div>
                <div className="h-px bg-border" />
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("nav.industries")}</p>
                  {industriesConfig.map((industry) => (
                    <a
                      key={industry.titleKey}
                      href={getHref(industry.href)}
                      className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {t(industry.titleKey)}
                    </a>
                  ))}
                </div>
                <div className="h-px bg-border" />
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{t("nav.openSource")}</p>
                  {openSourceConfig.map((tool) => {
                    const isExternal = tool.href.startsWith("http");
                    return (
                      <a
                        key={tool.titleKey}
                        href={isExternal ? tool.href : getHref(tool.href)}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {t(tool.titleKey)}
                      </a>
                    );
                  })}
                </div>
                <div className="h-px bg-border" />
                <div className="space-y-1">
                  <a href={getHref("/sovereign-ai")} className="block py-2.5 text-sm font-medium text-foreground" onClick={() => setIsOpen(false)}>
                    {t("nav.euPrivacy")}
                  </a>
                </div>
                <div className="h-px bg-border" />
                <div className="space-y-1">
                  <a 
                    href={getHref("/case-studies")}
                    className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors" 
                    onClick={() => setIsOpen(false)}
                  >
                    {t("nav.caseStudies")}
                  </a>
                  <a 
                    href={getHref("/insights")}
                    className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors" 
                    onClick={() => setIsOpen(false)}
                  >
                    {t("nav.insights")}
                  </a>
                  <a 
                    href={getHref("/blog")}
                    className="block py-2.5 text-sm font-medium text-foreground hover:text-primary transition-colors" 
                    onClick={() => setIsOpen(false)}
                  >
                    {t("nav.blog")}
                  </a>
                </div>
                <div className="pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs text-muted-foreground">
                      {t("common.location")}
                    </p>
                    <LanguageSwitcher />
                  </div>
                  <BookACallButton className="w-full" />
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
